import { computed, effect, Injectable, signal } from '@angular/core';
import type { EduTheme } from './theme.service';

export interface Customization {
  showPriceColumnInWebinarList?: CustomizationValue<boolean>;
  showPriceInWebinarOverview?: CustomizationValue<boolean>;
  showPaymentStatusColumnInParticipantList?: CustomizationValue<boolean>;
  /** Colour scheme; read and written through `ThemeService`. */
  theme?: CustomizationValue<EduTheme>;
}

export interface CustomizationValue<T> {
  value: T;
  isConfiguredByUser: boolean;
  configuredAt: Date;

  // eslint-disable-next-line edu/boolean-prefix -- persisted in localStorage; renaming drops stored values
  showHint?: boolean;
  requiredHintValue?: T;
  hintShownAt?: Date;
}

const CUSTOMIZATION_LOCAL_STORAGE_KEY = 'edu-dashboardCustomization';

@Injectable({
  providedIn: 'root',
})
export class CustomizationService {
  public customization = signal<Customization>({});

  public getCustomization<K extends keyof Customization>(key: K) {
    return computed(() => this.customization()?.[key]);
  }

  public getCustomizationsWithHint = computed(() => {
    const cu = this.customization();
    const result: Record<string, CustomizationValue<unknown>> = {};

    for (const key of Object.keys(cu) as (keyof Customization)[]) {
      const entry = cu[key];
      if (
        entry != null &&
        entry.showHint &&
        (entry.requiredHintValue === undefined || entry.value === entry.requiredHintValue)
      ) {
        result[key] = entry;
      }
    }

    return result;
  });

  constructor() {
    this.loadCustomizationFromLocalStorage();

    effect(() => {
      this.customization();
      this.persistCustomizationToLocalStorage();
    });
  }

  public updateCustomizationKey<K extends keyof Customization>(
    key: K,
    value: NonNullable<Customization[K]>['value'],
    opts?: {
      shouldOverrideIfConfiguredByUser?: boolean;
      shouldShowHint?: boolean;
      isConfiguredByUser?: boolean;
      requiredHintValue?: NonNullable<Customization[K]>['requiredHintValue'];
    },
  ) {
    // TS can't tie `value` to `K` once the entries differ in type (the theme is a
    // string); the signature already guarantees it.
    const newValue = {
      value,
      isConfiguredByUser: opts?.isConfiguredByUser ?? false,
      showHint: opts?.shouldShowHint ?? false,
      requiredHintValue: opts?.requiredHintValue,
      configuredAt: new Date(),
    } as NonNullable<Customization[K]>;

    const currentValue = this.customization();

    // Do not override if the value is configured by the user and the override flag is not set
    if (currentValue[key]?.isConfiguredByUser && !opts?.shouldOverrideIfConfiguredByUser) {
      return;
    }

    // Do not override if the value is the same
    if (currentValue[key]?.value === newValue.value) {
      return;
    }

    this.customization.update(cu => {
      return {
        ...cu,
        [key]: newValue,
      };
    });
  }

  public acknowledgeCustomizationHint<K extends keyof Customization>(key: K) {
    this.customization.update(cu => {
      return {
        ...cu,
        [key]: {
          ...cu[key],
          showHint: false,
          hintShownAt: new Date(),
        },
      };
    });
  }

  // Storage can be blocked (site data disabled) or hold a broken value; the app
  // must still start, now that the theme is read from here on every page.
  private loadCustomizationFromLocalStorage() {
    try {
      const customization = localStorage.getItem(CUSTOMIZATION_LOCAL_STORAGE_KEY);
      this.customization.set(customization ? (JSON.parse(customization) as Customization) : {});
    } catch (error) {
      console.warn('Dashboard customizations could not be loaded; using defaults.', error);
      this.customization.set({});
    }
  }

  private persistCustomizationToLocalStorage() {
    try {
      localStorage.setItem(CUSTOMIZATION_LOCAL_STORAGE_KEY, JSON.stringify(this.customization()));
    } catch (error) {
      // Not persisted; the customization still applies for this visit.
      console.warn('Dashboard customizations could not be saved.', error);
    }
  }
}
